import React from 'react';
import { SectionDefinition } from '../types/onboarding';

const makeIcon = (paths: React.ReactNode) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1f2937"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {paths}
  </svg>
);

export const onboardingSections: SectionDefinition[] = [
  {
    id: 'basic-info',
    title: "Let's get to know you",
    description: 'To begin your matching process, we need a few details about you.',
    icon: makeIcon(<path d="M12 12a4 4 0 100-8 4 4 0 000 8z" />),
    questions: [
      {
        id: 'seeker_over_18',
        label: 'Are you over 21?',
        type: 'singleselect',
        required: true,
        options: [
          { value: 'Yes', label: "Yes, I'm over 21" },
          { value: 'No', label: "No, I'm under 21" }
        ],
        helper: 'For legal and eligibility reasons, this service is available only to adults.'
      },
      {
        id: 'psychedelic_experience',
        label: 'Have you had any previous experience with psychedelics?',
        type: 'singleselect',
        required: true,
        helper: 'This question helps us understand your background so we can show you Guides whose education and support approach may be most relevant to you.',
        options: [
          'No prior experience',
          'Yes, in a personal or recreational setting',
          'Yes, with structured support or guidance',
          'Prefer not to say'
        ]
      },
      {
        id: 'seeker_gender',
        label: 'Please describe your gender identity.',
        type: 'singleselect',
        required: false,
        options: ['Female', 'Male', 'Transgender', 'Non-Binary', 'Prefer not to say'],
        helper: 'We ask this only so we can better understand your preferences and help identify guide profiles you may want to explore. There are no right or wrong answers. Sharing is voluntary.',
        disclaimer: "Guides Collective does not provide legal advice or determine legality in your jurisdiction. It's up to you to determine whether and how to engage."
      },
      {
        id: 'experience_types',
        label: 'What type of options are you considering?',
        type: 'multiselect',
        required: true,
        helper: "This helps us organize the guide profiles you'll see next.",
        disclaimer: "Guides Collective does not provide psychedelic therapy or harm-reduction services. We do not facilitate illegal psychedelic experiences or access to controlled substances. Guides operate independently, and users are responsible for deciding whether and how to engage.",
        options: [
          { value: 'Private / 1:1', label: 'Private / 1:1', sublabel: 'A private offering with one-on-one availability.' },
          { value: 'Group Setting', label: 'Group Setting', sublabel: 'A shared group-based guide offerings.' },
          { value: 'Retreat / Event Setting', label: 'Retreat / Event Setting', sublabel: 'Multi-day events hosted by independent guides.' },
          { value: 'Family / Couple Format', label: 'Family / Couple Format', sublabel: 'Guide offerings intended for the participants you choose.' },
          { value: 'Online Guide Availability', label: 'Online Guide Availability', sublabel: 'Remote communication options offered by some guides.' },
          { value: "I'm not sure yet", label: "I'm not sure yet" },
          { value: 'Other', label: 'Other' }
        ]
      },
      {
        id: 'medicine_options_comfort',
        label: 'Which types of guide offerings would you like to explore?',
        type: 'multiselect',
        required: true,
        helper: 'This helps determine which guide profiles may meet your preferences.',
        tooltip: {
          boldText: 'Guides collective does not provide medical, legal, or harm reduction advice, and does not endorse or facilitate the use of any substances.',
          regularText: 'Guides operate independently, and you are solely responsible for understanding and complying with applicable laws.'
        },
        disclaimer: 'Viewing guide profiles does not constitute a recommendation or guarantee of suitability.',
        options: [
          { value: 'Regulated options', label: 'Regulated options', sublabel: 'You prefer to explore profiles of guides who indicate they work within regulated frameworks (e.g., licensed therapists).' },
          { value: 'Open to all options', label: 'Open to all options', sublabel: "You'd like to view a wider range of guide profiles, understanding that availability and legality vary." }
        ]
      },
      {
        id: 'support_types',
        label: 'Which categories of guide offerings would you like to review?',
        type: 'multiselect',
        required: true,
        helper: "If you're not sure, that's completely okay. You can change these selections later.",
        disclaimer: 'Guides Collective does not provide medical, psychological, dosing, harm reduction, or therapeutic services. Guide profiles are informational only, and guides operate independently.',
        options: [
          {
            value: 'Microdose-Related Education & Coaching',
            label: 'Microdose-Related Education & Coaching',
            sublabel: 'Guide profiles that reference gentle, sub-perceptual micro-dose-related practices'
          },
          {
            value: 'Macrodose Options',
            label: 'Macrodose Options',
            sublabel: 'Guide profiles that reference higher-dose, immersive experiences'
          },
          {
            value: 'Preparation & Readiness Options',
            label: 'Preparation & Readiness Options',
            sublabel: 'Guide profiles that reference preparation offerings, such as setting intentions or assessing your personal readiness.'
          },
          {
            value: 'Integration Options',
            label: 'Integration Options',
            sublabel: 'Guides profiles that reference preparation offerings, such as setting intentions or assessing your personal readiness'
          },
          {
            value: 'Not sure',
            label: "I'm not sure yet"
          }
        ]
      },
      {
        id: 'travel_willingness',
        label: 'What are your location preferences?',
        type: 'multiselect',
        required: true,
        helper: "Choose all the options that feel right for you. There's no pressure. This just helps inform which profiles you'll see.",
        disclaimer: 'Guides Collective does not arrange, host, supervise, or facilitate sessions, travel, or experiences. Guide profiles are informational only, and guides operate independently.',
        options: [
          {
            value: 'At Home',
            label: 'At Home',
            sublabel: 'I would like to see profiles for guides open to coming to my home or private space.'
          },
          {
            value: 'Local Availability',
            label: 'Local Availability',
            sublabel: "I'm comfortable traveling to guides who indicate they work within my city or region."
          },
          {
            value: 'Virtual or Remote',
            label: 'Virtual or Remote',
            sublabel: "I'm looking for online preparation, integration, or other forms of remote communication."
          },
          {
            value: 'National Travel',
            label: 'National Travel',
            sublabel: 'I can travel within my country'
          },
          {
            value: 'International Travel',
            label: 'International Travel',
            sublabel: "I'm interested in exploring retreats outside of my country."
          },
          {
            value: 'Not Sure Yet',
            label: 'Not Sure Yet'
          }
        ]
      },
      {
        id: 'location_postcode',
        label: 'What is your ZIP / Postal Code?',
        type: 'text',
        required: true,
        helper: 'This helps us determine which guide profiles may align with your location or travel preferences. Your location does not limit the profiles you can explore – it simply identifies available guides.',
        disclaimer: "Guides Collective does not provide legal advice or determine legality in your jurisdiction. It's up to you to determine whether and how to engage."
      }
      // seeker_email, seeker_full_name, seeker_phone removed - now captured in create-account step
    ]
  },
  {
    id: 'experience-info',
    title: 'Experience preferences',
    description: 'This helps us understand the setting and level of support that feels right for you at this stage of your journey.',
    icon: makeIcon(
      <>
        <circle cx="8" cy="12" r="3" />
        <circle cx="16" cy="7" r="3" />
        <circle cx="16" cy="17" r="3" />
      </>
    ),
    questions: [
      {
        id: 'focus_preferences',
        label: 'What would you like to focus on?',
        type: 'grouped-multiselect',
        helper: 'You can choose as many as you like. There are no right or wrong answers.',
        disclaimer: 'Guides Collective does not arrange, host, supervise, or facilitate sessions, travel, or experiences. Guide profiles are informational only, and guides operate independently.',
        groups: [
          {
            id: 'focus_growth_pref',
            label: 'Personal Growth & Exploration',
            options: ['Personal Development', 'Empowerment', 'Creative Expression', 'Spiritual Exploration', 'Perspective Shifting']
          },
          {
            id: 'focus_mental_health_pref',
            label: 'Mental & Emotional Wellbeing',
            options: ['Anxiety', 'Depression', 'Mood Disorders', 'ADD / ADHD', 'OCD', 'Autism', 'Bipolar', 'Eating Disorders', 'General Mental Health Support']
          },
          {
            id: 'focus_challenging_pref',
            label: 'Challenging Life Experiences',
            options: [
              'PTSD',
              'Early Childhood Trauma',
              'Sexual Trauma',
              'Relationship Trauma',
              'Religious Trauma',
              'Racial Trauma',
              'Physical Trauma',
              'Cult / High-Control Group Recovery',
              'Shame'
            ]
          },
          {
            id: 'focus_behavioral_pref',
            label: 'Destructive Behavioral Patterns',
            options: ['Addiction / Substance Use', 'Anger Management']
          },
          {
            id: 'focus_relationships_pref',
            label: 'Relationships and Family',
            options: ['Couples Support', 'Divorce & Separation', 'Healthy Relationship Skills', 'Interpersonal Relationships', 'Parenting / Children']
          },
          {
            id: 'focus_transitions_pref',
            label: 'Life Transitions and Identity',
            options: [
              'Birth & Postpartum Support',
              'Career Support',
              'Life Transitions',
              'Grief & Loss',
              'Gender Identity',
              'End-of-Life Support'
            ]
          },
          {
            id: 'focus_unsure_pref',
            label: 'Unsure',
            options: ['Unsure Yet', 'Other']
          }
        ]
      },
      {
        id: 'prefer_specialist_in_groups',
        label: 'Are there any guide background attributes that matter to you?',
        type: 'multiselect',
        helper: 'Some seekers prefer guides who may have lived experience or identification with specific communities. This is completely optional — choose only if it matters to you.',
        disclaimer: 'Guide profile attributes are self-described and informational only. Guides Collective does not provide therapy, diagnosis, or treatment, and does not verify clinical qualifications.',
        options: ['BIPOC', 'LGBTQ+', 'Women', 'Men', 'Couples', 'Families', 'Veterans', 'Seniors', 'No Preference']
      },
      {
        id: 'guide_gender_preference',
        label: 'Some seekers prefer guides of a certain gender.',
        type: 'singleselect',
        helper: 'We will always do our best to connect you with a guide who fits your gender preference. However, availability can vary. Either way, you will be able to choose from profiles that align to your other stated preferences.',
        tooltip: {
          boldText: 'We will always do our best to connect you with a guide who fits your gender preference.',
          regularText: 'However, availability can vary. Either way, you will be able to choose from profiles that align to your other stated preferences.'
        },
        options: [
          { value: 'Female', label: 'Female Guide' },
          { value: 'Male', label: 'Male Guide' },
          { value: 'Non-binary', label: 'Non-binary Guide' },
          { value: 'No preference', label: 'No Preference' }
        ]
      },
      {
        id: 'religion_alignment_important',
        label: "Is it important that your guide's approach aligns with your religious or spiritual beliefs?",
        type: 'singleselect',
        required: true,
        helper: "Some seekers prefer a guide who shares or respects their worldview - whether that's religious, spiritual, secular, or somewhere in between. Answering this helps us organize guide profiles you may wish to explore.",
        disclaimer: 'Guide profiles reflect self-described perspectives. Guides collective does not evaluate belief alignment.',
        options: [
          { value: 'Yes', label: 'Yes', sublabel: "I'd like my guide to be sensitive to my beliefs" },
          { value: 'No', label: 'No', sublabel: "This isn't an important factor for me." }
        ]
      },
      {
        id: 'seeker_beliefs',
        label: 'Please select any religious or spiritual belief systems you identify with.',
        type: 'grouped-multiselect',
        showIf: { questionId: 'religion_alignment_important', equals: 'Yes' },
        helper: "This helps us identify guides who understand or reflect your worldview - if that's important to you.",
        disclaimer: 'Guide profiles reflect self-described backgrounds and perspectives. Guides Collective does not evaluate worldview compatibility.',
        standaloneOther: true,
        groups: [
          {
            id: 'beliefs_spiritual',
            label: 'Spiritual / Non Religious',
            options: ['Agnostic', 'Atheist', 'New Age / Spiritual']
          },
          {
            id: 'beliefs_major_religions',
            label: 'Major World Religions',
            options: ['Christian (Non-Denominational)', 'Catholic', 'Mormon / Latter-day Saint', 'Jewish', 'Muslim', 'Buddhist', 'Hindu']
          }
        ]
      }
    ]
  },
  {
    id: 'medical-info',
    title: 'Safety & medical context',
    description: 'This information helps us match you safely and responsibly with guides who are trained to support your specific health context.',
    icon: makeIcon(
      <>
        <path d="M12 2a7 7 0 00-7 7v2h14V9a7 7 0 00-7-7z" />
        <rect x="5" y="11" width="14" height="11" rx="3" />
        <path d="M10 16h4" />
      </>
    ),
    questions: [
      {
        id: 'currently_on_meds',
        label: 'Do you currently take any prescription medications (things like SSRIs, SNRIs, mood stabilizers, antipsychotics, anti-seizure medications or others that you believe may be relevant)?',
        type: 'singleselect',
        required: true,
        helper: 'This information is important as it allows us to identify guides who indicate experience working within your individual context.',
        tooltip: {
          boldText: 'Your information is safe.',
          regularText: 'Our privacy policy applies to all your responses, which are encrypted and shared only with the guide you choose to work with.'
        },
        disclaimer: 'Guides Collective does not provide medical advice, screening, safety assessment, or treatment. Users are responsible for consulting qualified healthcare professionals regarding medications and health decisions.',
        options: ['Yes', 'No']
      }
    ]
  },
  {
    id: 'additional-info',
    title: 'Final steps',
    description: 'Share anything else that will help us take great care of you.',
    icon: makeIcon(
      <>
        <path d="M4 4h16v16H4z" />
        <path d="M8 9l4 4 4-4" />
      </>
    ),
    questions: [
      {
        id: 'budget_band',
        label: 'Most guide packages run for three months. Please share your budget range for a three-month package.',
        type: 'singleselect',
        required: true,
        helper: 'This helps identify guides, programs, or retreat options that align with your budget expectations.',
        tooltip: {
          boldText: 'If you choose a lower budget range, you may be presented with profiles that include online courses or virtual conversations.',
          regularText: "High budget ranges may open access to guides who indicate they operate in-person, multi-day retreats, or more fulsome packages. Whatever your budget, you'll see guide profiles that best align with your preferences."
        },
        disclaimer: 'Pricing does not indicate quality, safety, or suitability. Guides Collective does not recommend or endorse any specific service. Our role is to organize and display guide profiles based on your stated preferences so you can independently explore options that feel aligned with your needs, interests, and circumstances.',
        options: [
          { value: '< $300', label: 'Under $300' },
          { value: '$300 - $2,000', label: '$300 - $2,000' },
          { value: '$2,000 - $5,000', label: '$2,000 - $5,000' },
          { value: '$5,000+', label: '$5,000+' },
          { value: 'view_all', label: "I'd like to view all budget ranges" }
        ]
      },
      {
        id: 'additional_info',
        label: "Is there anything else you'd like to share for context?",
        type: 'textarea',
        placeholder: 'Type here',
        helper: "You're welcome to share any additional, non-medical information related to your interests or preferences. You also have the option to leave this blank."
      },
      {
        id: 'referral_source',
        label: 'How did you hear about Guides Collective?',
        type: 'singleselect',
        helper: 'This helps us understand which channels are helping people discover the platform.',
        disclaimer: 'Listings do not imply partnerships or endorsements',
        options: [
          'Social Media Ad',
          'Google Search',
          'Podcast',
          'Friend or family referral',
          'Article or blog',
          'Therapist or coach referral',
          'Other'
        ]
      }
    ]
  }
];
